import {
  Button,
  Card,
  Chip,
  Grid,
  InputAdornment,
  TextField
} from '@material-ui/core';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import { Autocomplete } from '@material-ui/lab';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { BranchListDropDown } from 'src/components/dropdown';
import { purchaseOrderStatusOptionsV2 } from 'src/redux/slices/purchase-order';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { parseSearchV2, transformerDateField } from 'src/utils';
import { DatePickerRangeComponent } from 'src/components';
import { GetPOReportsPayload } from 'src/redux/slices/purchase-order/types';
import { useLocation, useNavigate } from 'react-router';

const { actions: purchaseOrderActions } = slices.purchaseOrder;

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

const {
  actions: supplierActions,
  selectors: supplierSelectors
} = slices.supplier;

export const POReportsToolbar: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const didHydrateFromURL = useRef(false);

  const categories = useAppSelector(categorySelectors.selectCategories);
  const supplierList = useAppSelector(supplierSelectors.selectSupplierList);

  const dateRangeFieldRef = useRef(null);

  const [inputUsersValue, setIputUsersValue] = useState<string[]>([]);
  const [branchIds, setBranchIds] = useState<number[]>([]);

  const [inputStatusValue, setInputStatusValue] = useState<string[]>([]);
  const [sku, setSku] = useState<string>('');

  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(
    false
  );

  const [toDate, setToDate] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [deliveryDateFrom, setDeliveryDateFrom] = useState<string>('');
  const [deliveryDateTo, setDeliveryDateTo] = useState<string>('');

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isSupplierLoading, setIsSupplierLoading] = useState<boolean>(false);
  const [supplierIds, setSupplierIds] = useState<number[]>([]);

  const [productName, setProductName] = useState<string>('');

  // For display category in UI
  const getNameFromId = (val: number, options: any[]) =>
    options.find((opt: any) => {
      console.log(val, opt, 'opt');
      return opt.id === val;
    })?.name;

  // For display category in UI
  const getIdFromName = (val: string, options: any[]) =>
    options.find((opt: any) => opt.name === val)?.id;

  // For display status in UI
  const getLabelFromValue = (val: string) =>
    purchaseOrderStatusOptionsV2.find((opt) => opt.value === val)?.label || val;

  // For sending to backend status
  const getValueFromLabel = (label: string) =>
    purchaseOrderStatusOptionsV2.find((opt) => opt.label === label)?.value ||
    label;

  const getCategories = useCallback(
    async (keyword?: string) => {
      try {
        setIsLoadingCategories(true);
        await dispatch(categoryActions.getCategoriesThunk({ keyword }));
        setIsLoadingCategories(false);
      } catch (error) {
        console.error(error);
        setIsLoadingCategories(false);
      }
    },
    [dispatch]
  );

  const onChangedDate = (
    date: MaterialUiPickersDate | undefined,
    field: 'from' | 'to'
  ) => {
    const transformedDate = date ? transformerDateField(date) : '';

    if (field === 'to') {
      setToDate(transformedDate);
    }
    if (field === 'from') {
      setFromDate(transformedDate);
    }
  };
  const onChangedDeliveryDate = (
    date: MaterialUiPickersDate | undefined,
    field: 'from' | 'to'
  ) => {
    const transformedDate = date ? transformerDateField(date) : '';

    if (field === 'to') {
      setDeliveryDateTo(transformedDate);
    }
    if (field === 'from') {
      setDeliveryDateFrom(transformedDate);
    }
  };

  const onApplyFilter = () => {
    let newQueryParams: GetPOReportsPayload = {
      start_date: fromDate || null,
      end_date: toDate || null,
      delivery_date_from: deliveryDateFrom || null,
      delivery_date_to: deliveryDateTo || null,
      supplier_id: supplierIds?.[0] ? supplierIds : null,
      branch_id: branchIds?.[0] ? branchIds : null,
      product_name: productName || null,
      sku: sku || null,
      status: inputStatusValue?.[0] ? inputStatusValue : null,
      created_by: inputUsersValue?.[0] ? inputUsersValue : null,
      category_id: categoryIds?.[0] ? categoryIds : null
    };

    const entries = Object.entries(newQueryParams).filter(
      ([, val]) => val !== null
    );

    const slug = entries.map((i) => `${i[0]}=${i[1]}`).join('&');

    navigate({ search: String(slug) }, { replace: true });
  };

  const stringToNumberArr = (val: string): number[] | null => {
    if (!val) {
      return null;
    }
    return val.split(',').map((i): number => +i) ?? null;
  };

  const renderParams = (params: GetPOReportsPayload) => {
    const {
      start_date,
      end_date,
      delivery_date_from,
      delivery_date_to,
      branch_id,
      product_name,
      sku,
      status,
      created_by
    } = params;

    if (start_date) setFromDate(start_date);
    if (end_date) setToDate(end_date);
    if (delivery_date_from) setDeliveryDateFrom(delivery_date_from);
    if (delivery_date_to) setDeliveryDateTo(delivery_date_to);
    if (branch_id) setBranchIds(branch_id);
    if (product_name) {
      setProductName(product_name);
    }
    if (sku) setSku(sku);
    if (status) setInputStatusValue(status);
    if (created_by) setIputUsersValue(created_by);
  };

  const getPOReports = async (payload: { [key: string]: string }) => {
    setIsLoadingData(true);
    const params: GetPOReportsPayload = {
      start_date: payload.start_date ?? null,
      end_date: payload.end_date ?? null,
      delivery_date_from: payload.delivery_date_from ?? null,
      delivery_date_to: payload.delivery_date_to ?? null,
      supplier_id: stringToNumberArr(payload?.supplier_id),
      branch_id: stringToNumberArr(payload?.branch_id),
      product_name: payload?.product_name ?? null,
      sku: payload.sku ?? null,
      status: payload?.status?.split(',') ?? null,
      created_by: payload?.created_by?.split(',') ?? null,
      category_id: stringToNumberArr(payload?.category_id),
      page: payload?.page ? +payload?.page : null
    };

    dispatch(purchaseOrderActions.setReportsParams(params));
    await dispatch(purchaseOrderActions.getReportsThunk(params));
    renderParams(params);
    setIsLoadingData(false);
  };

  const getSupplierOptions = async () => {
    setIsSupplierLoading(true);

    await dispatch(supplierActions.getSuppliersThunk({ limit: 9999 }));

    setIsSupplierLoading(false);
  };

  // Fetch category options
  useEffect(() => {
    if (!categories?.[0]) {
      getCategories();
    }
  }, [categories, getCategories]);

  // Fetch options for supplier
  useEffect(() => {
    getSupplierOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (search) {
      const payload = parseSearchV2(search);
      getPOReports(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Map certain filters
  useEffect(() => {
    if (!search || didHydrateFromURL.current) return;

    const payload = parseSearchV2(search);

    const newSupplierIds = stringToNumberArr(payload?.supplier_id);
    const newCategoryIds = stringToNumberArr(payload?.category_id);
    const areSuppliersLoaded = !!supplierList?.[0];
    const areCategoriesLoaded = !!categories?.[0];

    if (areCategoriesLoaded && areSuppliersLoaded) {
      setSupplierIds(newSupplierIds ?? []);
      setCategoryIds(newCategoryIds ?? []);
      didHydrateFromURL.current = true;
    }
  }, [categories, search, supplierList]);

  return (
    <Card style={{ padding: '1em' }}>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            value={productName}
            label="Product Name"
            name="product_name"
            onChange={(e) => setProductName(e.target.value)}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={4}>
          <BranchListDropDown
            paperSpace={0}
            isElevated={false}
            multiple={true}
            defaultValue={branchIds}
            onHandleBranchChange={(branchIds?: number[]) =>
              setBranchIds(branchIds || [])
            }
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            value={sku}
            label="SKU"
            name="sku"
            onChange={(e) => setSku(e.target.value)}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            id="supplier"
            multiple
            options={supplierList.map((c) => c.name)}
            disabled={isSupplierLoading}
            freeSolo
            value={supplierIds.map((i) => getNameFromId(i, supplierList))}
            renderTags={(value: any, getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  key={`${option}${index}`}
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            onChange={(_, newValue: any) => {
              setSupplierIds(
                newValue.map((i: any) => getIdFromName(i, supplierList))
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                label="Suppliers"
                name="suppliers"
                type="multiple"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="status"
            multiple
            options={purchaseOrderStatusOptionsV2.map((option) => option.label)}
            freeSolo
            value={inputStatusValue.map(getLabelFromValue)}
            renderTags={(value: any, getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  key={`${option}${index}`}
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            onChange={(_, newValue: any) => {
              setInputStatusValue(newValue.map(getValueFromLabel));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                label="Status"
                name="status"
                type="multiple"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="categories"
            multiple
            options={categories.map((c) => c.name)}
            disabled={isLoadingCategories}
            freeSolo
            value={categoryIds.map((i) => getNameFromId(i, categories))}
            renderTags={(value: any, getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  key={`${option}${index}`}
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            onChange={(_, newValue: any) => {
              setCategoryIds(
                newValue.map((i: any) => getIdFromName(i, categories))
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                label="Categories"
                name="categories"
                type="multiple"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="created-by"
            multiple
            options={[]}
            freeSolo
            value={inputUsersValue}
            renderTags={(value: any, getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  key={`${option}${index}`}
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            onChange={(_, newValue: any) => {
              setIputUsersValue(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                label="Created By"
                name="created_by"
                type="multiple"
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <div ref={dateRangeFieldRef}>
            <DatePickerRangeComponent
              title="Buy In Date Range"
              hasNoDateToday
              fromDateValue={fromDate || null}
              toDateValue={toDate || null}
              dateToLabel="End Date"
              dateFromLabel="Start Date"
              toDateMin={fromDate}
              onChangeToDate={(date) => onChangedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDate(date, 'from')}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <div ref={dateRangeFieldRef}>
            <DatePickerRangeComponent
              title="Delivery Date Range"
              hasNoDateToday
              fromDateValue={deliveryDateFrom || null}
              toDateValue={deliveryDateTo || null}
              dateToLabel="End Date"
              dateFromLabel="Start Date"
              toDateMin={deliveryDateFrom}
              onChangeToDate={(date) => onChangedDeliveryDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDeliveryDate(date, 'from')}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Button
            variant="contained"
            color={'primary'}
            onClick={onApplyFilter}
            disabled={isLoadingData}
          >
            Apply Filter
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};
